import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { useGetEngageJourneys } from '../../Hooks/useJourney';
import { useGetBlogPosts, useGetBlogPostsByIds } from '../../Hooks/useBlog';
import { useGetCMSPagesByIds } from '../../Hooks/useCMS';
import { getPathWithRootPath } from '../../Services/common';
import { CMSPageListItem } from '../../Services/cms';
import { BizEngageType, EngageObject } from '../../Types/engage';
import { ModuleData } from '../../Types/containerV2';
import {
	isObjectListModuleContent,
	ObjectListManualItem,
	ObjectListModuleContent,
	ObjectListObjectType,
} from '../../Types/containerV2/modules/objectList';
import { ObjectType } from '../../Types/base';
import { BlogPost, FetchBlogPostsOrder } from '../../Types/blog';
import { resolveJourney } from '../Journey/journeyLookup';
import { useContainerContext } from './ContainerContext';
import './ObjectListModule.scss';

type ObjectListModuleProps = {
	module: ModuleData;
};

const DEFAULT_LIMIT = 3;

type ObjectListDisplayItem = {
	title: string;
	description: string;
	imageUrl: string;
	urlRewrite: string;
	url: string;
};

const ObjectListModule = ({ module }: ObjectListModuleProps) => {
	const { journeys, statusState } = useGetEngageJourneys();
	const { objectId, objectType } = useContainerContext();
	const content = isObjectListModuleContent(module.content) ? module.content : null;
	const usesGenericCategories = content?.source === 'genericCategories';
	const usesManualSelection = content?.source === 'manual';
	const manualItems = content?.manualItems ?? [];
	const manualPageIds = manualItems.filter((item) => item.objectType === 'cmsPage').map((item) => item.objectId);
	const manualBlogIds = manualItems.filter((item) => item.objectType === 'blog').map((item) => item.objectId);
	const { pages: manualPages, statusState: manualPagesStatus } = useGetCMSPagesByIds(manualPageIds, usesManualSelection);
	const { blogPosts: manualBlogs, statusState: manualBlogsStatus } = useGetBlogPostsByIds(manualBlogIds, usesManualSelection);
	const top = getEffectiveTop(content?.top);
	const blogPosts = useGetBlogPosts(
		{
			order: FetchBlogPostsOrder.Latest,
			overrideCache: false,
			pageIndex: 0,
			pageSize: top,
			genericCategoryIds: content?.genericCategoryIds ?? [],
		},
		usesGenericCategories
	);

	if (!content) {
		return null;
	}

	const currentObjectType = mapObjectTypeToEngageType(objectType);
	const selectedJourney = useMemo(
		() => resolveJourney(journeys, objectId, currentObjectType),
		[journeys, objectId, currentObjectType]
	);
	const journeyItems = useMemo(
		() => getJourneyObjectsForModule(selectedJourney?.EngageObjects ?? [], content, objectId, currentObjectType),
		[selectedJourney, content, objectId, currentObjectType]
	);
	const blogItems = useMemo(() => mapBlogPostsToDisplayItems(blogPosts ?? [], top), [blogPosts, top]);
	const manualSelectionItems = useMemo(
		() => getManualItems(manualItems, manualPages, manualBlogs, objectId, objectType),
		[manualItems, manualPages, manualBlogs, objectId, objectType]
	);
	const items = usesManualSelection ? manualSelectionItems : usesGenericCategories ? blogItems : journeyItems;
	const listHtml = useMemo(
		() =>
			items
				.map((item) => DOMPurify.sanitize(renderItemTemplate(content.itemTemplate, item)))
				.join(''),
		[items, content.itemTemplate]
	);
	const customStyles = getCustomStyles(module.guid, content.customCss, content.mobileCustomCss);

	const manualSelectionIsLoading =
		usesManualSelection &&
		((manualPageIds.length > 0 && manualPagesStatus === 'LOADING') ||
			(manualBlogIds.length > 0 && manualBlogsStatus === 'LOADING'));

	if (
		manualSelectionIsLoading ||
		(!usesManualSelection && !usesGenericCategories && statusState === 'LOADING') ||
		(usesGenericCategories && !blogPosts)
	) {
		return <div className='biz-module biz-object-list-module biz-object-list-module--state'>Loading...</div>;
	}

	if (items.length === 0) {
		if (!content.emptyStateText?.trim()) {
			return null;
		}

		return <div className='biz-module biz-object-list-module biz-object-list-module--empty'>{content.emptyStateText}</div>;
	}

	return (
		<div className='biz-module biz-object-list-module' data-module-guid={module.guid}>
			{customStyles && <style>{customStyles}</style>}
			<ul className='biz-object-list-module__list' dangerouslySetInnerHTML={{ __html: listHtml }} />
		</div>
	);
};

function getEffectiveTop(top?: number) {
	return Number.isFinite(top) && top! > 0 ? Math.floor(top!) : DEFAULT_LIMIT;
}

function mapBlogPostsToDisplayItems(posts: BlogPost[], top: number): ObjectListDisplayItem[] {
	return posts.slice(0, top).map((post) =>
		mapEngageObjectToDisplayItem({
			Id: post.Id,
			Name: post.Title,
			Description: post.Description,
			ImageUrl: post.ImageUrl ?? '',
			UrlRewrite: post.UrlRewrite,
			EngageObjectType: BizEngageType.Blog,
			EstimatedTimeInMinutes: 0,
			IsActive: true,
		})
	);
}

function getJourneyObjectsForModule(
	objects: EngageObject[],
	content: ObjectListModuleContent,
	currentObjectId?: number,
	currentObjectType?: BizEngageType
) {
	const configuredLimit = content.limit ?? 0;
	const effectiveLimit = Number.isFinite(configuredLimit) && configuredLimit > 0 ? Math.floor(configuredLimit) : DEFAULT_LIMIT;
	const allowedTypes = new Set(getEnabledEngageTypes(content.objectTypes));

	return objects
		.filter((item) => allowedTypes.has(item.EngageObjectType))
		.filter(
			(item) =>
				!(
					currentObjectId &&
					currentObjectType &&
					item.Id === currentObjectId &&
					item.EngageObjectType === currentObjectType
				)
		)
		.slice(0, effectiveLimit)
		.map(mapEngageObjectToDisplayItem);
}

function getManualItems(
	configuredItems: ObjectListManualItem[],
	pages: CMSPageListItem[],
	blogs: BlogPost[],
	currentObjectId?: number,
	currentObjectType?: ObjectType
): ObjectListDisplayItem[] {
	const pagesById = new Map(pages.map((page) => [page.Id, page]));
	const blogsById = new Map(blogs.map((blog) => [blog.Id, blog]));

	return configuredItems.flatMap((configuredItem) => {
		if (configuredItem.objectId === currentObjectId && isCurrentManualItem(configuredItem, currentObjectType)) {
			return [];
		}

		if (configuredItem.objectType === 'cmsPage') {
			const page = pagesById.get(configuredItem.objectId);
			return page ? [mapCMSPageToDisplayItem(page)] : [];
		}

		const blog = blogsById.get(configuredItem.objectId);
		return blog ? [mapBlogPostToDisplayItem(blog)] : [];
	});
}

function isCurrentManualItem(item: ObjectListManualItem, currentObjectType?: ObjectType) {
	return (
		(item.objectType === 'cmsPage' && currentObjectType === ObjectType.CMSPage) ||
		(item.objectType === 'blog' && currentObjectType === ObjectType.BlogPost)
	);
}

function mapCMSPageToDisplayItem(page: CMSPageListItem): ObjectListDisplayItem {
	return {
		title: page.Name,
		description: page.CMSDescription ?? '',
		imageUrl: page.ImageUrl ?? '',
		urlRewrite: page.UrlRewrite,
		url: getPathWithRootPath(`/page/${encodeURIComponent(page.UrlRewrite || String(page.Id))}`),
	};
}

function mapEngageObjectToDisplayItem(item: EngageObject): ObjectListDisplayItem {
	return {
		title: item.Name ?? '',
		description: item.Description ?? '',
		imageUrl: item.ImageUrl ?? '',
		urlRewrite: item.UrlRewrite ?? '',
		url: resolveObjectUrl(item),
	};
}

function mapBlogPostToDisplayItem(post: BlogPost): ObjectListDisplayItem {
	return mapBlogPostsToDisplayItems([post], 1)[0];
}

function getEnabledEngageTypes(objectTypes: ObjectListObjectType[]) {
	const effectiveTypes = objectTypes.length > 0 ? objectTypes : ['article'];

	return effectiveTypes.flatMap((type) => {
		switch (type) {
			case 'article':
				return [BizEngageType.Blog];
			case 'education':
				return [BizEngageType.Education];
			case 'quiz':
				return [BizEngageType.Speedquiz];
			default:
				return [];
		}
	});
}

function renderItemTemplate(template: string, item: ObjectListDisplayItem) {
	const replacements: Record<string, string> = {
		title: escapeHtml(item.title),
		url: escapeHtml(item.url),
		image: escapeHtml(resolveImageUrl(item.imageUrl)),
		imageThumb: escapeHtml(resolveImageUrl(item.imageUrl, 'sm')),
		description: escapeHtml(item.description),
		urlRewrite: escapeHtml(item.urlRewrite),
	};

	return Object.entries(replacements).reduce((html, [key, value]) => {
		const moustachePattern = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
		const hashPattern = new RegExp(`##\\s*${key}\\s*##`, 'gi');
		return html.replace(moustachePattern, value).replace(hashPattern, value);
	}, template);
}

function getCustomStyles(moduleGuid: string, customCss?: string, mobileCustomCss?: string) {
	const selector = `.biz-object-list-module[data-module-guid="${moduleGuid}"] .biz-object-list-module__list`;
	const desktopStyles = buildScopedCss(selector, customCss);
	const mobileStyles = buildScopedCss(selector, mobileCustomCss);

	return [
		desktopStyles,
		mobileStyles ? `@media (max-width: 767px) { ${mobileStyles} }` : '',
	]
		.filter(Boolean)
		.join('\n');
}

function buildScopedCss(selector: string, css?: string) {
	const value = css?.trim();
	if (!value || /<\/style/i.test(value) || value.includes('@')) return '';

	if (!value.includes('&') && !value.includes('{') && !value.includes('}')) {
		return `${selector} { ${value} }`;
	}

	const firstScopedRuleIndex = value.indexOf('&');
	if (firstScopedRuleIndex < 0) return '';

	const rootDeclarations = value.slice(0, firstScopedRuleIndex).trim();
	const scopedRules = value.slice(firstScopedRuleIndex).trim();
	const rootStyles = rootDeclarations && !/[{}]/.test(rootDeclarations) ? `${selector} { ${rootDeclarations} }` : '';

	if (countOccurrences(scopedRules, '{') !== countOccurrences(scopedRules, '}') || !allSelectorsAreScoped(scopedRules)) {
		return rootStyles;
	}

	return [rootStyles, scopedRules.replace(/&/g, selector)].filter(Boolean).join('\n');
}

function allSelectorsAreScoped(css: string) {
	const selectors = [...css.matchAll(/(?:^|})([^{}]+)\{/g)].map((match) => match[1].trim());
	return selectors.length > 0 && selectors.every((selector) => selector.includes('&'));
}

function countOccurrences(value: string, character: string) {
	return value.split(character).length - 1;
}

function resolveObjectUrl(item: EngageObject) {
	switch (item.EngageObjectType) {
		case BizEngageType.Blog:
			return getPathWithRootPath(`/blog/${encodeURIComponent(item.UrlRewrite || String(item.Id))}`);
		case BizEngageType.Education:
			return getPathWithRootPath('/educations');
		case BizEngageType.Speedquiz:
			return getPathWithRootPath('/quiz');
		default:
			return '#';
	}
}

function resolveImageUrl(imageUrl?: string, size?: 'sm') {
	if (!imageUrl) return '';
	return getPathWithRootPath(imageUrl, size);
}

function escapeHtml(value: string) {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function mapObjectTypeToEngageType(objectType?: ObjectType) {
	switch (objectType) {
		case ObjectType.BlogPost:
			return BizEngageType.Blog;
		case ObjectType.OnlineEducation:
			return BizEngageType.Education;
		case ObjectType.Quiz:
			return BizEngageType.Speedquiz;
		case ObjectType.MediabankFile:
			return BizEngageType.Video;
		default:
			return undefined;
	}
}

export default ObjectListModule;
